import * as qs from "query-string";
import React, { useCallback } from "react";
import { WPPost, CoordType } from "../../../constants/types";
import { ListingAcf } from "../../../constants/types/acf";
import { useBaseUrl, useFreeServiceVisibility, useTaxonomies } from "../../../services/app-state";
import { filterListingsByRadius } from "../../../services/app-state/services";
import { fetchJSON } from "../../../utils/ajax";
import { useSortingCoords } from "../../../utils/hooks";
import { DirectoryQueryData } from "../HeroDirectory";

type FetchNextPageStateType = {
    function: undefined | (() => Promise<void>);
};

type ListingEndpointResponse = {
    total_post_count: number;
    has_next_page: boolean;
    listings: WPPost<ListingAcf>[];
};

export const useDirectorySearch = () => {
    const [totalListings, setTotalListings] = React.useState(0);
    const sortCoords = useSortingCoords();
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState();
    const [listings, setListings] = React.useState<WPPost<ListingAcf>[]>([]);
    const [fetchNextPage, setFetchNextPage] = React.useState<FetchNextPageStateType>({
        function: undefined,
    });
    const baseUrl = useBaseUrl();
    const [{ communitiesBySlug, conditionsBySlug, gendersBySlug }] = useTaxonomies();
    const [showingFreeService] = useFreeServiceVisibility();
    
    const fetchData = useCallback(
        async (data: {
            query: DirectoryQueryData;
            page?: number;
            action?: "APPEND" | "REPLACE";
            location?: CoordType;
            per_page?: number;
        }) => {
            const { query, page = 1, action = "REPLACE" } = data;
            setLoading(true);
            setError(undefined);
            
            try {
                const queryData = {
                    page,
                    per_page: data.per_page || 30,
                    lat: data.location?.lat,
                    lng: data.location?.lng,
                    community: communitiesBySlug[query.community]?.id,
                    condition: conditionsBySlug[query.condition]?.id,
                    gender: gendersBySlug[query.gender]?.id,
                    age: query.age,
                    type: "physical_address",
                    free: showingFreeService
                };
                const queryString = qs.stringify(queryData, { encode: false });
                const url = baseUrl + "/wp-json/headstart/v1/listings?" + queryString;

                if (queryData.page == 1) {
                    setListings([]);
                }

                const response = await fetchJSON<ListingEndpointResponse>(url);

                //Filter the posts by 100KM radius
                const sortedListings = filterListingsByRadius(response?.listings, sortCoords);
                
                // If there's more results to load, create a a fetchNextPage function
                if (response.has_next_page) {
                    setFetchNextPage({
                        function: () =>
                            fetchData({
                                ...data,
                                page: page + 1,
                                action: "APPEND",
                            }),
                    });
                } else {
                    setFetchNextPage({ function: undefined });
                }

                switch (action) {
                    case "APPEND":
                        setListings(old => [...old, ...sortedListings]);
                        break;
                    case "REPLACE":
                    default:
                        setListings(() => sortedListings);
                        break;
                }

                setTotalListings(response.total_post_count);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        },
        [baseUrl, communitiesBySlug, conditionsBySlug, gendersBySlug, showingFreeService, sortCoords],
    );
        
    const loadResults = useCallback(
        async (query: DirectoryQueryData, coords: CoordType | void, perPage?: number) => {
            setFetchNextPage({ function: undefined });
            await fetchData({
                query,
                location: coords || undefined,
                action: "REPLACE",
                per_page: perPage,
            });
        },
        [fetchData],
    );

    return {
        loading,
        error,
        listings,
        totalListings,
        loadMore: fetchNextPage.function,
        loadResults,
    };
};
